import { useEffect, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { clinicalDelegationService } from '../../../services/clinicalDelegationService';

export interface DelegationDoctorIds {
  /** Doctors the assistant may write for right now (`can_act`, mirrors canActAsDoctor). */
  actingDoctorIds: number[];
  /** Assignments with `can_sign = true` (may correct their own delegated signatures, R3). */
  consentedDoctorIds: number[];
  /** Doctors the assistant may sign for right now (`can_sign_delegated`: consent AND the legal flag, R12). */
  delegatedSignerDoctorIds: number[];
}

const EMPTY: DelegationDoctorIds = { actingDoctorIds: [], consentedDoctorIds: [], delegatedSignerDoctorIds: [] };

/**
 * The current user's delegation doctor ids, from one my-doctors request. Doctors
 * never need it (their authority never depends on an assignment), so they
 * skip the request entirely. `delegatedSignerDoctorIds` stays empty while
 * CLINICAL_DELEGATED_SIGNING is off, whatever the consent says.
 */
export function useDelegationDoctorIds(): DelegationDoctorIds {
  const { user } = useAuth();
  const [ids, setIds] = useState<DelegationDoctorIds>(EMPTY);
  const needsAssignments = !!user && !user.doctor;

  useEffect(() => {
    if (!needsAssignments) return;
    let active = true;
    clinicalDelegationService.getMyDoctors().then((doctors) => {
      if (!active) return;
      setIds({
        actingDoctorIds: doctors.filter((d) => d.can_act).map((d) => d.id),
        consentedDoctorIds: doctors.filter((d) => d.can_sign).map((d) => d.id),
        delegatedSignerDoctorIds: doctors.filter((d) => d.can_sign_delegated).map((d) => d.id),
      });
    });
    return () => { active = false; };
  }, [needsAssignments]);

  return ids;
}

/** Doctors the current user may still correct delegated signatures for (R3). */
export function useConsentedDoctorIds(): number[] {
  return useDelegationDoctorIds().consentedDoctorIds;
}
